import { NgModule, inject } from '@angular/core';
import { RouterModule, Routes, ActivatedRouteSnapshot, Router, CanActivateFn, ResolveFn } from '@angular/router';
import { ErrorComponent } from './components/pages/error/error.component';
import { BusinessService } from './services/business.service';
import { firstValueFrom } from 'rxjs';
import { DemosComponent } from './components/pages/demos/demos.component';
import { DynamicTemplateComponent } from './components/pages/dynamic-template/dynamic-template.component';

function getHostedBranchKey(): string | null {
  if (typeof window === 'undefined') return null;

  const hostname = window.location.hostname.toLowerCase();

  // Legacy format: home-test.qode.qa
  const homeMatch = hostname.match(/^home-([a-z0-9-]+)\.qode\.qa$/);
  if (homeMatch) return homeMatch[1];

  // New format: test.qode.qa (direct subdomain landing page)
  const qodeMatch = hostname.match(/^([a-z0-9][a-z0-9-]*)\.qode\.qa$/);
  if (qodeMatch) {
    const sub = qodeMatch[1];
    const reserved = ['www', 'api', 'admin', 'servio', 'menu'];
    if (!reserved.some(r => sub === r || sub.startsWith(r + '-'))) return sub;
  }

  // Custom domain (e.g. bracket.qa) — look up by custom_domain in backend
  if (!hostname.endsWith('.qode.qa')) return hostname;

  return null;
}

const resolveHostedLanding: CanActivateFn = async () => {
  const router = inject(Router);
  const businessService = inject(BusinessService);
  const branchKey = getHostedBranchKey();

  if (!branchKey) return router.createUrlTree(['/home']);

  try {
    const resolved = await firstValueFrom(businessService.resolveMenu(branchKey));
    const landingId = resolved.chain_id || resolved.branch_id;
    if (!landingId) return router.createUrlTree(['/not-found']);

    await router.navigate([`/${landingId}`], { skipLocationChange: true });
    return false;
  } catch {
    return router.createUrlTree(['/not-found']);
  }
};

const validateBusiness: CanActivateFn = async (route: ActivatedRouteSnapshot) => {
  const router = inject(Router);
  const businessService = inject(BusinessService);
  const businessId = route.paramMap.get('businessId');

  if (!businessId || isNaN(+businessId)) {
    return router.createUrlTree(['/not-found']);
  }

  try {
    const data: any = await firstValueFrom(businessService.getLandingPage(+businessId));

    // Business ID was passed but it belongs to a chain — redirect to chain URL
    if (data._redirect_to_chain) {
      if (getHostedBranchKey()) {
        await router.navigate([`/${data._redirect_to_chain}`], { skipLocationChange: true });
        return false;
      }
      return router.createUrlTree([`/${data._redirect_to_chain}`]);
    }

    if (!data.template_id) {
      return router.createUrlTree(['/not-found'], {
        queryParams: { businessId, error: 'no-template' }
      });
    }
    return true;
  } catch (error) {
    console.error('Failed to validate business:', error);
    return router.createUrlTree(['/not-found'], {
      queryParams: { businessId, error: 'not-found' }
    });
  }
};

const resolveTemplateId: ResolveFn<string> = async (route: ActivatedRouteSnapshot) => {
  const businessService = inject(BusinessService);
  const businessId = route.paramMap.get('businessId');
  const path = route.routeConfig?.path || '';
  const templateFromUrl = path.split('/')[0];

  if (!businessId || isNaN(+businessId)) return '';

  try {
    const data: any = await firstValueFrom(businessService.getLandingPage(+businessId));
    if (data._redirect_to_chain) return ''; // guard will redirect before this matters
    const templateId = data.template_id?.toLowerCase() || '';
    if (templateId === 'all' && templateFromUrl.match(/^home-(one|two|three|four|five|six|seven|eighth|nine)$/)) {
      return templateFromUrl;
    }
    return templateId;
  } catch {
    return '';
  }
};

const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    component: DynamicTemplateComponent,
    canActivate: [resolveHostedLanding]
  },
  { path: 'home', component: DemosComponent },
  // Must stay above ':businessId', otherwise '/not-found' is matched as a business id
  { path: 'error', component: ErrorComponent },
  { path: 'not-found', component: ErrorComponent },
  {
    path: ':businessId',
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-one/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-two/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-three/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-four/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-five/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-six/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-seven/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-eighth/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { 
    path: 'home-nine/:businessId', 
    component: DynamicTemplateComponent,
    canActivate: [validateBusiness],
    resolve: { templateId: resolveTemplateId }
  },
  { path: '**', component: ErrorComponent }
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule]
})
export class AppRoutingModule { }
